import { useState } from 'react';
import { PROMOTION_PERMISSIONS } from '@/constants/permissions';
import { CatalogueListLayout } from '@/components/common/CatalogueListLayout';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { DataTable } from '@/components/common/DataTable';
import { ErrorState } from '@/components/common/ErrorState';
import { useInfiniteTableQuery } from '@/hooks/useInfiniteTableQuery';
import { formatCount } from '@/utils/number';
import { promoItemTypesInfiniteQueryOptions } from '../api/promotion.api';
import { getPromoItemTypeColumns } from '../components/PromoColumns';
import { PromoItemTypeFormModal } from '../components/PromoFormModals';
import { usePromoCatalogueFilters } from '../hooks/usePromoCatalogueFilters';
import { useDeletePromoItemType } from '../hooks/usePromoMutations';
import type { PromoItemType } from '../types/promotion.types';
import { getPromoItemTypeStatCards } from '../utils/promotionStatCards';

function getDeleteMessage(itemType: PromoItemType | null): string {
  if (itemType === null) return '';
  if (itemType.itemCount > 0) {
    return `"${itemType.name}" has ${formatCount(itemType.itemCount, 'item')}, so you cannot delete it. Make it inactive instead.`;
  }
  return `"${itemType.name}" will be deleted. You cannot undo this.`;
}

export function PromoItemTypePage() {
  const controller = usePromoCatalogueFilters('itemTypes');
  const { rows, firstPageMeta, isLoading, isRefetching, loadError, refetch, loadMoreProps } =
    useInfiniteTableQuery(promoItemTypesInfiniteQueryOptions(controller.params));

  const [editing, setEditing] = useState<PromoItemType | null>(null);
  const [isFormOpen, setFormOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<PromoItemType | null>(null);

  const remove = useDeletePromoItemType(() => setPendingDelete(null));

  const columns = getPromoItemTypeColumns(controller.search.search, {
    onEdit: (row) => {
      setEditing(row);
      setFormOpen(true);
    },
    onDelete: setPendingDelete,
  });

  return (
    <CatalogueListLayout
      title="Promotional Item Type"
      search={{
        value: controller.searchText,
        onChange: controller.setSearchText,
        placeholder: 'Search item types',
      }}
      filters={controller}
      stats={{
        cards: getPromoItemTypeStatCards(firstPageMeta),
        isLoading,
        isFetching: isRefetching,
        isError: loadError !== null,
      }}
      addRequires={PROMOTION_PERMISSIONS.ITEM_TYPE_CREATE}
      onAdd={() => {
        setEditing(null);
        setFormOpen(true);
      }}
      addLabel="Add Item Type"
    >
      {loadError ? (
        <ErrorState
          error={loadError}
          fallbackTitle="Could not load the item types"
          onRetry={() => void refetch()}
          fill
        />
      ) : (
        <DataTable
          data={rows}
          columns={columns}
          isLoading={isLoading}
          className="lg:flex-1"
          stickyColumnId="name"
          emptyMessage="No item type matches"
          {...loadMoreProps}
        />
      )}

      <PromoItemTypeFormModal itemType={editing} open={isFormOpen} onOpenChange={setFormOpen} />

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(open) => !open && setPendingDelete(null)}
        title="Delete item type?"
        description={getDeleteMessage(pendingDelete)}
        confirmLabel="Delete"
        confirmVariant="destructive"
        isPending={remove.isPending}
        pendingLabel="Deleting..."
        onConfirm={() => pendingDelete && remove.mutate(pendingDelete.id)}
      />
    </CatalogueListLayout>
  );
}
